import type { ReputationVerdict } from "@sage/shared";

export interface SampleRow {
  id: number;
  sha256: string;
  size: number; // bytes
  type: string;
  verdict: ReputationVerdict;
  firstSeen: string; // ISO date
  lastScanned: string;
}

function rng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s;
  };
}

// prettier-ignore
const sha256s = [
  "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855",
  "d7a8fbb307d7809469ca9abcb0082e4f8d5651e46d3cdb762d02d0bf37c9e592",
  "6dcd4ce23d88e2ee9568ba546c007c63f187fe3e8e5aaf239a3eb74c5d912c4e",
  "2cf24dba5fb0a30e26e83b2ac5b9e29e1b161e5c1fa7425e73043362938b9824",
  "5e884898da28047151d0e56f8dc6292773603d0d6aabbdd62a11ef721d1542d8",
  "a665a45920422f9d417e4867efdc4fb8a04a1f3fff1fa07e998e86f7f7a27ae3",
  "35a9e381b1a27567549b5f8a6f783c167ebf809f1c4d6a9e367240484d198ce4",
  "8c6976e5b5410415bde908bd4dee15dfb167a9c873fc4bb8a81f6f2ab448a918",
  "b94d27b9934d3e08a52e52d7da7dabfac484efe37a5380ee9088f7ace2efcde9",
  "9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08",
  "e99a18c428cb38d5f260853678922e03abd8336883c4cd0be7586f11b4f7e8c5",
  "e7f6c011776e8db7cd330b54174fd76f7d0216b612387a5ffcfb81e6f0919683",
  "7902699be42c8a8e46fbbb45167262a2e4efade748f3b6e58e1d12ea2d5a7e6b",
  "2c8e1d3a61c1a1d3b240c06a5093f8a3c9a2c3f04b7ec6e6b4b6a1d2c4f5a6b7",
  "4e07408562bedb8b60ce05c1decfe3ad16b72230967de01f640b7e4729b49fce",
  "e8d5b5a7c2f3e4d6a8b7c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0",
  "a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2",
  "f1e2d3c4b5a60718f9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b3a2f1e0d9c8b7a6",
  "abc123def456abc123def456abc123def456abc123def456abc123def456abc123",
  "deadbeefcafebabedeadbeefcafebabedeadbeefcafebabedeadbeefcafebabe0000",
  "1111111111111111111111111111111111111111111111111111111111111111",
];

const types = [
  "PE32 executable",
  "ELF 64-bit",
  "PDF document",
  "ZIP archive",
  "Office Open XML",
  "Mach-O 64-bit",
  "text/plain",
  "image/png",
  "application/javascript",
  "MS-DOS executable",
];

const verdicts: ReputationVerdict[] = [
  "clean",
  "clean",
  "clean",
  "clean",
  "clean",
  "clean",
  "clean",
  "clean",
  "clean",
  "clean",
  "suspicious",
  "suspicious",
  "suspicious",
  "malicious",
  "malicious",
  "malicious",
  "unknown",
  "unknown",
];

export function generateSamples(): SampleRow[] {
  const rand = rng(42);
  const now = Date.now();

  const sizeTiers = [8192, 16384, 65536, 128000, 512000, 1200000, 2400000, 5120000, 10240000, 18874368];

  return sha256s.slice(0, 18).map((sha256, i) => {
    const daysAgo = Math.floor((rand() % 30) + 1);
    const daysAgoScanned = Math.floor(rand() % daysAgo);

    return {
      id: i + 1,
      sha256,
      size: sizeTiers[i % sizeTiers.length]!,
      type: types[i % types.length]!,
      verdict: verdicts[i % verdicts.length]!,
      firstSeen: new Date(now - daysAgo * 86400000).toISOString(),
      lastScanned: new Date(now - daysAgoScanned * 86400000).toISOString(),
    };
  });
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function truncateSha256(hash: string): string {
  return `${hash.slice(0, 6)}...${hash.slice(-8)}`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
